import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { 
    Plus, Trash2, Edit, Save, ChevronLeft, ChevronRight,
    Instagram, Facebook, Youtube, Linkedin, Twitter, Globe,
    BarChart3, Upload, FileText, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsSection from './ReportsSection';

const channels = [
    { id: 'instagram', name: 'Instagram', icon: Instagram },
    { id: 'facebook', name: 'Facebook', icon: Facebook },
    { id: 'youtube', name: 'YouTube', icon: Youtube },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin },
    { id: 'twitter', name: 'Twitter/X', icon: Twitter },
    { id: 'tiktok', name: 'TikTok', icon: Globe }
];

const ReportsManager = ({ customer, onClose, embedded = false }) => {
    const { api } = useAuth();
    const [reports, setReports] = useState([]);
    const [pdfs, setPdfs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReportModal, setShowReportModal] = useState(false);
    const [editingReport, setEditingReport] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [reportForm, setReportForm] = useState({
        month: selectedMonth,
        channel: 'instagram',
        clicks: 0,
        reach: 0,
        impressions: 0,
        engagement: 0,
        followers_gained: 0,
        likes: 0,
        comments: 0,
        shares: 0,
        saves: 0,
        video_views: 0,
        story_views: 0,
        profile_visits: 0,
        website_clicks: 0,
        notes: ''
    });

    const fetchData = useCallback(async () => {
        try {
            const [reportsRes, pdfsRes] = await Promise.all([
                api.get(`/customers/${customer.id}/reports`),
                api.get(`/customers/${customer.id}/content-plan/pdfs`)
            ]);
            setReports(reportsRes.data);
            setPdfs(pdfsRes.data);
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    }, [api, customer.id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openCreateReport = () => {
        setEditingReport(null);
        setReportForm({
            month: selectedMonth,
            channel: 'instagram',
            clicks: 0,
            reach: 0,
            impressions: 0,
            engagement: 0,
            followers_gained: 0,
            likes: 0,
            comments: 0,
            shares: 0,
            saves: 0,
            video_views: 0,
            story_views: 0,
            profile_visits: 0,
            website_clicks: 0,
            notes: ''
        });
        setShowReportModal(true);
    };

    const openEditReport = (report) => {
        setEditingReport(report);
        setReportForm({
            month: report.month,
            channel: report.channel,
            clicks: report.clicks || 0,
            reach: report.reach || 0,
            impressions: report.impressions || 0,
            engagement: report.engagement || 0,
            followers_gained: report.followers_gained || 0,
            likes: report.likes || 0,
            comments: report.comments || 0,
            shares: report.shares || 0,
            saves: report.saves || 0,
            video_views: report.video_views || 0,
            story_views: report.story_views || 0,
            profile_visits: report.profile_visits || 0,
            website_clicks: report.website_clicks || 0,
            notes: report.notes || ''
        });
        setShowReportModal(true);
    };

    const handleSaveReport = async () => {
        try {
            await api.post(`/customers/${customer.id}/reports`, reportForm);
            toast.success(editingReport ? 'Report aktualisiert!' : 'Report erstellt!');
            setShowReportModal(false);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const handleDeleteReport = async (reportId) => {
        if (!window.confirm('Report wirklich löschen?')) return;
        
        try {
            await api.delete(`/customers/${customer.id}/reports/${reportId}`);
            toast.success('Report gelöscht');
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handlePdfUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!file.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Nur PDF-Dateien erlaubt');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            await api.post(`/customers/${customer.id}/content-plan/pdf?month=${selectedMonth}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            toast.success('PDF hochgeladen!');
            fetchData();
        } catch (error) {
            toast.error('Upload fehlgeschlagen');
        } finally {
            setUploading(false);
        }
    };

    const handleDeletePdf = async (pdfId) => {
        if (!window.confirm('PDF wirklich löschen?')) return;
        
        try {
            await api.delete(`/customers/${customer.id}/content-plan/pdf/${pdfId}`);
            toast.success('PDF gelöscht');
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const navigateMonth = (direction) => {
        const [year, month] = selectedMonth.split('-').map(Number);
        const newDate = new Date(year, month - 1 + direction, 1);
        setSelectedMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
    };

    const formatMonth = (monthStr) => {
        if (!monthStr) return '';
        const [year, month] = monthStr.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    };

    const filteredReports = reports.filter(r => r.month === selectedMonth);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold">Reports & PDFs</h2>
                        <p className="text-sm text-muted-foreground">{customer.name}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-medium px-3 min-w-[140px] text-center capitalize">
                        {formatMonth(selectedMonth)}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b">
                <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'overview' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    Übersicht
                </button>
                <button
                    onClick={() => setActiveTab('data')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'data' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    Daten eingeben
                </button>
                <button
                    onClick={() => setActiveTab('pdfs')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'pdfs' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    PDFs
                </button>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <ReportsSection customerId={customer.id} isPublic={false} />
            )}

            {/* Data Entry Tab */}
            {activeTab === 'data' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <Button onClick={openCreateReport}>
                            <Plus className="w-4 h-4 mr-2" />
                            Neuer Eintrag
                        </Button>
                    </div>

                    {filteredReports.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Keine Daten für {formatMonth(selectedMonth)}</p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {filteredReports.map((report) => {
                                const channel = channels.find(c => c.id === report.channel);
                                const ChannelIcon = channel?.icon || Globe;
                                
                                return (
                                    <Card key={report.id} className="shadow-soft">
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <ChannelIcon className="w-5 h-5" />
                                                    <span className="font-medium">{channel?.name || report.channel}</span>
                                                </div>
                                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                                    <span>Reichweite: {report.reach?.toLocaleString('de-DE')}</span>
                                                    <span>Klicks: {report.clicks?.toLocaleString('de-DE')}</span>
                                                    <span>Engagement: {report.engagement}%</span>
                                                    <Button variant="ghost" size="sm" onClick={() => openEditReport(report)}>
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteReport(report.id)} className="text-destructive">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* PDFs Tab */}
            {activeTab === 'pdfs' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <label className="cursor-pointer">
                            <Button disabled={uploading}>
                                {uploading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                )}
                                PDF hochladen
                            </Button>
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={handlePdfUpload}
                                className="hidden"
                            />
                        </label>
                    </div>

                    {pdfs.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Keine PDFs hochgeladen</p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {pdfs.map((pdf) => (
                                <Card key={pdf.id} className="shadow-soft">
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <FileText className="w-5 h-5 text-red-500" />
                                                <div>
                                                    <p className="font-medium">{pdf.filename}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {pdf.month ? formatMonth(pdf.month) : 'Allgemein'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button variant="outline" size="sm" asChild>
                                                    <a href={pdf.url} target="_blank" rel="noopener noreferrer">
                                                        Anzeigen
                                                    </a>
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => handleDeletePdf(pdf.id)}
                                                    className="text-destructive"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Report Modal */}
            <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingReport ? 'Report bearbeiten' : 'Neuer Report-Eintrag'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Monat</Label>
                                <Input
                                    type="month"
                                    value={reportForm.month}
                                    onChange={(e) => setReportForm({ ...reportForm, month: e.target.value })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Kanal</Label>
                                <Select 
                                    value={reportForm.channel} 
                                    onValueChange={(v) => setReportForm({ ...reportForm, channel: v })}
                                >
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {channels.map(channel => (
                                            <SelectItem key={channel.id} value={channel.id}>
                                                <div className="flex items-center gap-2">
                                                    <channel.icon className="w-4 h-4" />
                                                    {channel.name}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Reichweite</Label>
                                <Input
                                    type="number"
                                    value={reportForm.reach}
                                    onChange={(e) => setReportForm({ ...reportForm, reach: parseInt(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Impressionen</Label>
                                <Input
                                    type="number"
                                    value={reportForm.impressions}
                                    onChange={(e) => setReportForm({ ...reportForm, impressions: parseInt(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Klicks</Label>
                                <Input
                                    type="number"
                                    value={reportForm.clicks}
                                    onChange={(e) => setReportForm({ ...reportForm, clicks: parseInt(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Engagement (%)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={reportForm.engagement}
                                    onChange={(e) => setReportForm({ ...reportForm, engagement: parseFloat(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Neue Follower</Label>
                                <Input
                                    type="number"
                                    value={reportForm.followers_gained}
                                    onChange={(e) => setReportForm({ ...reportForm, followers_gained: parseInt(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Likes</Label>
                                <Input
                                    type="number"
                                    value={reportForm.likes}
                                    onChange={(e) => setReportForm({ ...reportForm, likes: parseInt(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Kommentare</Label>
                                <Input
                                    type="number"
                                    value={reportForm.comments}
                                    onChange={(e) => setReportForm({ ...reportForm, comments: parseInt(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Shares</Label>
                                <Input
                                    type="number"
                                    value={reportForm.shares}
                                    onChange={(e) => setReportForm({ ...reportForm, shares: parseInt(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Saves</Label>
                                <Input
                                    type="number"
                                    value={reportForm.saves}
                                    onChange={(e) => setReportForm({ ...reportForm, saves: parseInt(e.target.value) || 0 })}
                                    className="mt-1"
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowReportModal(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSaveReport}>
                            <Save className="w-4 h-4 mr-2" />
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ReportsManager;
